import { when } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { AnalyticsConsumerProps } from '../../hoc/withAnalytics';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import ProductStore, { Site } from '../../store/productStore';
import { JoinableProduct } from '../../ui/Products/JoinableProductCard';

const joinSiteExperimentFlagKey = 'uchi.enable-join-site-experiment';

// We require props for store and analytics to avoid redundant component wrapping.
// Our HoC will tend to be used by components that already wrap themselves in the appropriate HoCs:
//
//   inject(analytics(withJoinSiteExperiment(ComponentToWrap)))
//
// If the experiment HoC wrapped itself with inject/analytics, we'd end up with redundancy:
//
//   inject(analytics(inject(analytics(withJoinSiteExperiment(ComponentToWrap)))))
//
type RequiredOuterProps = {
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
} & AnalyticsConsumerProps;

interface ConsumerExperimentProp {
  fireExposureEvent: (details: ExposureEventArgs) => void;
  hasLoadedCohorting: boolean;
  isLoading: boolean;
  isVariation: boolean;
}

export interface JoinSiteExperimentConsumerProps {
  joinSiteExperiment: ConsumerExperimentProp;
}

interface ExposureEventArgs {
  treatment: 'home' | 'home-interim' | 'ineligible';
  utmSource: string;
}

export const withJoinSiteExperiment = <OriginalProps extends RequiredOuterProps>(
  ComponentToWrap: React.ComponentType<OriginalProps & JoinSiteExperimentConsumerProps>,
) => {
  @observer
  class WrappedComponent extends React.Component<OriginalProps> {
    fireExposureEvent = ({ utmSource, treatment }: ExposureEventArgs): void => {
      when(this.hasLoaded, () => {
        const { availableProducts, availableSites, joinableProducts, joinableSites } = this.props.productStore;
        const hasJoinableProducts = joinableProducts.length > 0 && joinableSites.length > 0;

        let reason = this.getExperiment().isVariation ? 'RULE_MATCH' : 'RULE_NOT_MATCH';
        if (!hasJoinableProducts) {
          reason = 'NO_JOINABLE_PRODUCTS';
        }
        this.props.analytics.track('feature', 'exposed', {
          flagKey: joinSiteExperimentFlagKey,
          reason,
          ruleId: 'dummy', // Required by MEP
          value: this.getExperiment().isVariation,

          treatment: hasJoinableProducts ? treatment : 'ineligible',
          utmSource,

          availableProductsCount: availableProducts.length,
          availableSitesCount: availableSites.length,
          joinableProducts: getProductsAnalyticsData(joinableSites),
          joinableProductsCount: joinableProducts.length,
          joinableSitesCount: joinableSites.length,
        });
      });
    };

    getExperiment = (): ConsumerExperimentProp => ({
      fireExposureEvent: this.fireExposureEvent,
      hasLoadedCohorting: this.props.featureFlagsStore.hasLoaded,
      isLoading: !this.hasLoaded(),
      isVariation: !!this.props.featureFlagsStore.flags[joinSiteExperimentFlagKey], // server doesn't send feature flags that are false
    });

    hasLoaded = () => this.props.featureFlagsStore.hasLoaded && this.props.productStore.hasLoaded;

    render() {
      return <ComponentToWrap {...this.props} joinSiteExperiment={this.getExperiment()} />;
    }
  }

  return WrappedComponent;
};

type ProductsAnalyticsData = ProductsOnSiteAnalyticsData[];
interface ProductsOnSiteAnalyticsData {
  cloudId: string;
  products: string[];
}

export const getProductsAnalyticsData = (sites: Site<JoinableProduct>[]): ProductsAnalyticsData => {
  return sites.map(({ cloudId, products }) => ({
    cloudId,
    products: products.map(product => product.productKey),
  }));
};
